import { useCallback, useEffect, useRef, useState } from "react";
import { getDraft, setDraft } from "@/lib/drafts";
import { MAX_SELECTED_SKILLS, normalizeSelectedSkills } from "@/lib/selected-skills";
import { fetchWithAuth, type AccessTokenGetter } from "@/lib/utils";

export interface AgentSkill { name: string; description: string }
export type AgentSkillsStatus = "loading" | "ready" | "unavailable";
interface SkillResult { status: AgentSkillsStatus; skills: AgentSkill[] }
const cache = new Map<string, Promise<SkillResult>>();

function parseSkills(value: unknown): SkillResult {
  if (!value || typeof value !== "object" || !Array.isArray((value as { skills?: unknown }).skills)) return { status: "unavailable", skills: [] };
  const entries = (value as { skills: unknown[] }).skills;
  if (entries.some((entry) => !entry || typeof entry !== "object" || typeof (entry as AgentSkill).name !== "string" || typeof (entry as AgentSkill).description !== "string")) return { status: "unavailable", skills: [] };
  const byName = new Map<string, AgentSkill>();
  for (const entry of entries as AgentSkill[]) {
    const name = entry.name.trim();
    if (name && !byName.has(name)) byName.set(name, { name, description: entry.description });
  }
  return { status: "ready", skills: [...byName.values()].sort((a, b) => a.name.localeCompare(b.name)) };
}

function loadSkills(agentId: string, getToken: AccessTokenGetter): Promise<SkillResult> {
  let pending = cache.get(agentId);
  if (!pending) {
    pending = fetchWithAuth(`/api/agents/${encodeURIComponent(agentId)}/capabilities`, { cache: "no-store" }, getToken)
      .then(async (response) => response.ok ? parseSkills(await response.json()) : { status: "unavailable" as const, skills: [] })
      .catch(() => ({ status: "unavailable" as const, skills: [] }));
    cache.set(agentId, pending);
  }
  return pending;
}

export function useAgentSkills(agentId: string | null, sessionId: string, getToken: AccessTokenGetter) {
  const [result, setResult] = useState<SkillResult>({ status: "unavailable", skills: [] });
  const [selected, setSelected] = useState<string[]>(() => getDraft(sessionId).selectedSkills);
  const previousAgent = useRef(agentId);
  const selectedRef = useRef(selected);
  selectedRef.current = selected;

  useEffect(() => {
    if (previousAgent.current !== agentId) {
      previousAgent.current = agentId;
      setSelected([]);
      const draft = getDraft(sessionId);
      if (draft.selectedSkills.length) setDraft(sessionId, draft.text, []);
    } else {
      setSelected(getDraft(sessionId).selectedSkills);
    }
  }, [agentId, sessionId]);

  useEffect(() => {
    let live = true;
    setResult({ status: agentId ? "loading" : "unavailable", skills: [] });
    if (agentId) void loadSkills(agentId, getToken).then((loaded) => {
      if (!live) return;
      setResult(loaded);
      setSelected((current) => {
        const valid = loaded.status === "ready"
          ? current.filter((name) => loaded.skills.some((skill) => skill.name === name)) : [];
        if (valid.length !== current.length) {
          const draft = getDraft(sessionId);
          setDraft(sessionId, draft.text, valid);
        }
        return valid;
      });
    });
    return () => { live = false; };
  }, [agentId, getToken, sessionId]);

  const update = useCallback((next: string[]) => {
    const normalized = normalizeSelectedSkills(next);
    selectedRef.current = normalized;
    setSelected(normalized);
    const draft = getDraft(sessionId);
    setDraft(sessionId, draft.text, normalized);
  }, [sessionId]);
  const toggle = useCallback((name: string) => {
    if (result.status !== "ready" || !result.skills.some((skill) => skill.name === name)) return;
    const current = selectedRef.current;
    if (current.includes(name)) update(current.filter((value) => value !== name));
    else if (current.length < MAX_SELECTED_SKILLS) update([...current, name]);
  }, [result, update]);
  const remove = useCallback((name: string) => update(selectedRef.current.filter((value) => value !== name)), [update]);
  const reset = useCallback(() => update([]), [update]);
  const clearIfMatches = useCallback((names: string[]) => {
    const current = selectedRef.current;
    if (current.length === names.length && current.every((name, index) => name === names[index])) update([]);
  }, [update]);
  const restore = useCallback((names: string[]) => update(result.status === "ready" ? names.filter((name) => result.skills.some((skill) => skill.name === name)) : []), [result, update]);
  return { status: result.status, skills: result.skills, selected, toggle, remove, reset, restore, clearIfMatches, limitReached: selected.length >= MAX_SELECTED_SKILLS };
}
